import { useState, useEffect } from 'react';
import { Box, Flex, IconButton, Button, Link, Tag, Text } from '@chakra-ui/react';
import { MdDelete, MdSearch, MdEdit } from 'react-icons/md';
import { ExternalLinkIcon } from '@chakra-ui/icons';

import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import StrongInput from '../components/common/StrongInput';
import ThumbnailPreview from '../components/common/ThumbnailPreview';
import EditResourceModal from '../components/resources/EditResourceModal';

const { getSectionstitle: getProgramsTitleUrl } =           
  urlConstants.equipments;

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const RESOURCE_COLUMNS = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'fileName',
    label: 'File Name',
    render: (value: string) => (
      <Text noOfLines={1}>{value || '-'}</Text>
    ),
  },
  {
    key: 'mimeType',
    label: 'Type',
    render: (value: string) => (
      <Tag size='sm' colorScheme='blue'>
        {value || 'Unknown'}
      </Tag>
    ),
  },
  {
    key: 'fileSize',
    label: 'Size',
    render: (value: number) => formatFileSize(value),
  },
  {
    key: 'fileUrl',
    label: 'File',
    render: (value: string) =>
      value ? (
        <Link href={value} isExternal color='primary.600' fontWeight='medium'>
          Open File <ExternalLinkIcon mx='2px' />
        </Link>
      ) : (
        '-'
      ),
  },
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    render: (value: string) =>
      value ? <ThumbnailPreview imageUrl={value} /> : '-',
  },
  {
    key: 'description',
    label: 'Description',
    render: (value: string) => <Text noOfLines={2}>{value || '-'}</Text>,
  },
];

const {
  createResource: createResourceUrl,
  getResources: getResourcesUrl,
  editResource: editResourceUrl,
  deleteResource: deleteResourceUrl,
} = urlConstants.resources

const Resources = () => {
  const [resources, setResources] = useState<Resource[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedResource, setSelectedResource] = useState<Resource>();

  const [programstitle, setProgramsTitle] =                   
    useState<ProgramTitle[]>([]);
  
  
  const showToast = useToastNotification();

  const { loading: isGetResourcesLoading, sendRequest: getResources } =
    useApi<GetResourcesResponse>();
  const { loading: isCreateResourceLoading, sendRequest: createResource } =
    useApi<any>();
  const { loading: isEditResourceLoading, sendRequest: editResource } =
    useApi<any>();
  const { loading: isDeleteResourceLoading, sendRequest: deleteResource } =
    useApi<any>();

  const { sendRequest: getProgramstitle } =                   
    useApi<ProgramTitle[]>();
  
  
  const fetchResources = () => {
    getResources({
      callback: (data: GetResourcesResponse | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setResources(data.resources);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      },
      command: ApiCommand.GET,
      url: getResourcesUrl,
      options: {
        page: currentPage,
        limit: pageSize,
      },
    });
  };

  const fetchProgramsTitles = () => {                         
    getProgramstitle({               
      callback: (data, error) => {
        if (data) setProgramsTitle(data);                     
      },                                 
      command: ApiCommand.GET,                                
      url: getProgramsTitleUrl,
    });
  }; 
  

  useEffect(() => {
    fetchResources();
    fetchProgramsTitles();
  }, [currentPage, pageSize]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onSaveResource = (
    title: string,
    description: string,
    thumbnail: File | null,
    resourceFile: File | null,
    selectedprograms: string[],
  ) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    if (resourceFile) {
      formData.append('file', resourceFile);
    }
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }

    formData.append('programs',                                 
      JSON.stringify(selectedprograms));
    
    if (selectedResource) {
      formData.append('resourceId', selectedResource?._id || '');
      editResource({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            //TODO create toast.constants.ts file and create variables for all toast description
            description: 'Resource updated successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchResources();
        },
        command: ApiCommand.PUT,
        url: editResourceUrl,
        options: formData,
      });
    } else {
      createResource({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'Resource created successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchResources();
        },
        command: ApiCommand.POST,
        url: createResourceUrl,
        options: formData,
      });
    }
  };

  const onDeleteResource = (resourceId: string) => {
    deleteResource({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        showToast({
          title: 'Success',
          description: 'Resource deleted successfully',
          status: 'success',
        });
        fetchResources();
      },
      command: ApiCommand.DELETE,
      url: deleteResourceUrl,
      options: {
        resourceId,
      },
    });
  };

  const onEditResource = (resourceId: string) => {
    setSelectedResource(resources.find(({ _id }) => _id === resourceId));
    setIsOpenModal(true);
  };

  return (
    <Page>
      <Flex
        justifyContent='space-between'
        alignItems='center'
        mb={6}
        gap={4}
        flexWrap='wrap'
      >
        <Box flex='1' maxW='400px'>
          <StrongInput
            placeholder='Search'
            rightIcon={<MdSearch color='gray.500' />}
          />
        </Box>
        <Button
          colorScheme='primary'
          px={6}
          py={4}
          borderRadius='md'
          fontWeight='bold'
          _hover={{ bg: 'primary.300' }}
          onClick={() => {
            setSelectedResource(undefined);
            setIsOpenModal(true);
          }}
        >
          Create Resource
        </Button>
      </Flex>

      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box
          backgroundColor='white'
          borderRadius='lg'
          height='calc(100vh - 275px)'
        >
          <StrongTable
            columns={RESOURCE_COLUMNS}
            data={resources}
            loading={isGetResourcesLoading}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit Resource'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEditResource(row._id)}
                />
                <IconButton
                  aria-label='Delete Resource'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => onDeleteResource(row._id)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditResourceModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSaveResource}
        selectedResource={selectedResource}
        programstitle={programstitle} 
      />
    </Page>
  );
};

export default Resources;
